import React, { useMemo, useState, useEffect } from "react";
import Column from "./components/Column";
import AddNewColumn from "./components/AddNewColumn";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { DRAG_AND_DROP_CONSTANTS } from "utils/dndBoard/constants";
import { connect } from "react-redux";
import { removeMultiplePages, reorderThumbnails } from "utils/redux/actions";
import Trashcan from "./components/Trashcan";
import DivaPage from "./components/DivaPage";
import { multiSelect } from "utils/dndBoard/multiSelect";
import { mutliDragAwareReorder } from "utils/dndBoard/mutliDragAwareReorder";
import { bulkDelete } from "utils/dndBoard/bulkDelete";
import { flow, valuesIn, flatten, xor } from "lodash/fp";
import ConfirmModal from "components/ConfirmModal";
import { Badge } from "shards-react";
import { last, transform } from "lodash";

const Board = ({
  dispatch,
  dndData,
  showNotDetected,
  setPageToDisplay,
  saveHandler,
  viewerImages,
  showComment,
  sortByPage,
  diva,
}) => {
  const [selectedPages, setSelectedPages] = useState({});
  const [draggingPageId, setDraggingPageId] = useState(false);
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const disableDrag = window.location.pathname.slice(-1) === "1";
  const getColumn = (id) => dndData?.columns[id];
  const getPages = (column) =>
    column?.pageIds.map((pageName) => dndData.pages[pageName]);
  const handleDragStart = ({ draggableId }) => {
    const selected = pageIdsSelected.includes(draggableId);
    if (!selected) unselectAll(); // if dragging an item that is not selected - unselect all items

    return setDraggingPageId(draggableId);
  };

  const pageIdsSelected = useMemo(() => {
    const extractedIds = flow(valuesIn, flatten)(selectedPages);
    return extractedIds;
  }, [selectedPages]);

  const handleDragEnd = (result) => {
    const { destination, source, reason, type, draggableId } = result;
    if (!destination || reason === "CANCEL") return removeDraggingId();

    if (destination.droppableId === DRAG_AND_DROP_CONSTANTS.TRASHCAN)
      return setConfirmBulkDelete(true);

    const { updatedData, orderedSelectedPageIds } = mutliDragAwareReorder({
      entities: dndData,
      selectedPages,
      draggableId,
      destination,
      source,
      type,
      pageIdsSelected,
    });

    dispatch(reorderThumbnails(updatedData));
    setSelectedPages(orderedSelectedPageIds);
    removeDraggingId();

    return;
  };

  const closeBulkModal = () => {
    removeDraggingId();
    unselectAll();
    return setConfirmBulkDelete(false);
  };

  const handleBulkDelete = () => {
    const newOrder = bulkDelete({ entities: dndData, selectedPages });

    saveHandler({ newOrder, handleRemovedPage });
  };

  const handleRemovedPage = (response) => {
    dispatch(reorderThumbnails(response));

    const updatedPages = transform(
      pageIdsSelected,
      (result, pageId) => {
        const pageNumber = pageId.split("-")[1];

        result[pageNumber] = {
          ...viewerImages[pageNumber],
          isDeleted: true,
        };
      },
      {}
    );

    dispatch(removeMultiplePages(updatedPages));

    return closeBulkModal();
  };

  const toggleSelection = ({ pageId, columnId }) => {
    const pageNotSelected = !pageIdsSelected.includes(pageId);
    const pageWasPartOfASelectedGroup = pageIdsSelected.length > 1;
    const newPageIds =
      pageNotSelected || pageWasPartOfASelectedGroup
        ? { [columnId]: [pageId] }
        : {};

    return setSelectedPages(newPageIds);
  };

  const toggleSelectionInGroup = ({ pageId, columnId }) => {
    const { [columnId]: selectedColumn, ...rest } = selectedPages;
    const newPages = xor([pageId], selectedColumn);
    const result = { ...rest };

    if (newPages.length) result[columnId] = newPages;

    return setSelectedPages(result);
  };

  const multiSelectTo = ({ pageId, columnId }) => {
    const updated = multiSelect({
      column: dndData.columns[columnId],
      selectedPages,
      pageId,
      columnId,
    }); // This behaviour matches the MacOSX finder selection
    if (!updated) return;

    return setSelectedPages(updated);
  };

  const onWindowKeyDown = (e) => {
    if (e.defaultPrevented) return;
    if (e.keyCode === 27) unselectAll();
    return;
  };

  const onWindowClick = (e) => {
    if (e.defaultPrevented || selectedPages.length === 0 || confirmBulkDelete)
      return;
    return unselectAll();
  };

  const unselectAll = () => setSelectedPages({});
  const removeDraggingId = () => setDraggingPageId(false);
  const preventSelect = (e) => e.preventDefault();

  useEffect(() => {
    window.addEventListener("click", onWindowClick);
    window.addEventListener("keydown", onWindowKeyDown);
    window.addEventListener("selectstart", preventSelect);
    return () => {
      window.removeEventListener("click", onWindowClick);
      window.removeEventListener("keydown", onWindowKeyDown);
      window.removeEventListener("selectstart", preventSelect);
    };
  });

  return (
    <DragDropContext
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      style={{ marginLeft: "auto", marginRight: "auto" }}
    >
      {dndData?.columnOrder && (
        <Droppable
          droppableId="board"
          direction={DRAG_AND_DROP_CONSTANTS.DIRECTION}
          type={DRAG_AND_DROP_CONSTANTS.COLUMN}
        >
          {(provided) => (
            <section
              className={`d-flex justify-content-center align-items-start max-width h-max-content ${
                !showNotDetected && diva ? "ml-auto" : "mx-auto"
              }`}
              ref={provided.innerRef}
              {...provided.droppableProps}
            >
              {dndData?.columnOrder?.map((columnId, index) => {
                const column = getColumn(columnId);
                const pages = getPages(column);
                return (
                  <Draggable
                    key={columnId}
                    index={index}
                    draggableId={columnId}
                    isDragDisabled={disableDrag}
                  >
                    {(provided) => (
                      <Column
                        pages={pages}
                        column={column}
                        draggableProps={provided.draggableProps}
                        dragHandleProps={provided.dragHandleProps}
                        innerRef={provided.innerRef}
                        toggleSelection={toggleSelection}
                        toggleSelectionInGroup={toggleSelectionInGroup}
                        multiSelectTo={multiSelectTo}
                        draggingPageId={draggingPageId}
                        setPageToDisplay={setPageToDisplay}
                        disableDrag={disableDrag}
                        saveHandler={saveHandler}
                        pageIdsSelected={pageIdsSelected}
                        showComment={showComment}
                        sortByPage={sortByPage}
                      />
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
              {dndData && showNotDetected && (
                <Column
                  pages={getPages(
                    dndData?.columns[DRAG_AND_DROP_CONSTANTS.UNORDERED_TYPE]
                  )}
                  column={
                    dndData?.columns[DRAG_AND_DROP_CONSTANTS.UNORDERED_TYPE]
                  }
                  toggleSelection={toggleSelection}
                  toggleSelectionInGroup={toggleSelectionInGroup}
                  multiSelectTo={multiSelectTo}
                  pageIdsSelected={pageIdsSelected}
                  draggingPageId={draggingPageId}
                  setPageToDisplay={setPageToDisplay}
                  disableDrag={disableDrag}
                  saveHandler={saveHandler}
                />
              )}
              {pageIdsSelected.length > 1 && <Trashcan />}
              {!disableDrag && <AddNewColumn />}
            </section>
          )}
        </Droppable>
      )}
      {confirmBulkDelete && (
        <ConfirmModal
          message={
            <>
              <span className="mr-2">Möchten Sie die</span>
              <Badge className="rounded-circle" outline theme="danger">
                {pageIdsSelected.length}
              </Badge>
              <span className="ml-2">Seiten wirklich löschen?</span>
            </>
          }
          confirmFunction={() => handleBulkDelete()}
          closeModal={() => closeBulkModal()}
        />
      )}
      {diva && (
        <div
          className={`d-flex flex-column max-width h-max-content px-1 rounded border-2 border-transparent mr-2 ${
            !showNotDetected && dndData?.columnOrder ? "mr-auto" : "mx-auto"
          }`}
          style={{ backgroundColor: "#E3E3E3" }}
        >
          <span className="text-center font-weight-bold text-black my-1">
            DiVA - Anzahl Seiten: {diva.divaTotalPages}
          </span>
          {Object.keys(diva.divaThumbnails).map((key) => {
            const divaPgNum =
              Number(key.split("-")[key.split("-").length - 1]) + 1;
            const originalKey = key.replace(
              /thumbnail-\d{1,}/g,
              `original-${divaPgNum}`
            );
            const isSelected = pageIdsSelected?.includes(originalKey);
            return (
              <DivaPage
                key={key}
                id={originalKey}
                setPageToDisplay={setPageToDisplay}
                thumbnail={diva.divaThumbnails[key]}
                isSelected={isSelected}
                toggleSelection={toggleSelection}
                divaPgNum={divaPgNum}
              />
            );
          })}
        </div>
      )}
    </DragDropContext>
  );
};

// Take thumbnails from redux state
const mapStateToProps = (state) => {
  const { approved, thumbnails, pages, diva } = state.savedDocument;

  return {
    showNotDetected: !approved,
    dndData: thumbnails,
    viewerImages: pages,
    diva,
  };
};

export default connect(mapStateToProps)(Board); //Connect the component with redux and export it.
